const PIXEL_ATTR = 'data-pixel-script';
const CURRENCY = 'BRL';

/**
 * Mesmos formatos aceitos pela API em `EventPixel::PIXEL_ID_FORMATS`. O ID entra
 * dentro de uma tag <script>, então um valor fora do formato é execução de
 * JavaScript arbitrário na página do evento. A API já recusa no cadastro; esta é
 * a segunda barreira, para dados gravados antes de a validação existir.
 */
const PIXEL_ID_FORMATS = {
    meta: /^\d{10,20}$/,
    google: /^(?:G|AW|GTM|UA|DC)-[A-Za-z0-9]{4,20}(?:-[A-Za-z0-9]{1,10})?$/,
    tiktok: /^[A-Za-z0-9]{10,30}$/,
};

/* Cada plataforma nomeia as mesmas conversões de um jeito. `Purchase` só existe
   com esse nome na Meta: no Google é `purchase` e no TikTok é `CompletePayment`
   — mandar "Purchase" para o TikTok cria um evento personalizado, que não conta
   como conversão para a campanha. */
const googleEventMap = {
    AddToCart: 'add_to_cart',
    InitiateCheckout: 'begin_checkout',
    AddPaymentInfo: 'add_payment_info',
    Lead: 'generate_lead',
    Purchase: 'purchase',
};

const tiktokEventMap = {
    AddToCart: 'AddToCart',
    InitiateCheckout: 'InitiateCheckout',
    AddPaymentInfo: 'AddPaymentInfo',
    Lead: 'SubmitForm',
    Purchase: 'CompletePayment',
};

let storedPixels = [];
const injectedPixels = new Set();
const firedEvents = new Set();

function appendScript(name, content) {
    const script = document.createElement('script');
    script.setAttribute(PIXEL_ATTR, name);
    script.textContent = content;
    document.head.appendChild(script);
}

function ensureMetaBase() {
    if (window.fbq) {
        return;
    }

    appendScript('meta-base', `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
    `);
}

function injectMetaPixel(pixelId) {
    ensureMetaBase();

    window.fbq('init', pixelId);
    // `trackSingle` em vez de `track`: com dois pixels Meta no mesmo evento,
    // `track` dispararia o PageView nos dois.
    window.fbq('trackSingle', pixelId, 'PageView');
}

function ensureGoogleBase(pixelId) {
    if (window.gtag) {
        return;
    }

    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(pixelId)}`;
    loader.setAttribute(PIXEL_ATTR, 'google-loader');
    document.head.appendChild(loader);

    appendScript('google-base', `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
    `);
}

function injectGooglePixel(pixelId) {
    ensureGoogleBase(pixelId);
    window.gtag('config', pixelId);
}

function ensureTikTokBase() {
    if (window.ttq) {
        return;
    }

    // Snippet oficial sem o `ttq.load`/`ttq.page` do final: com mais de um pixel
    // do TikTok no evento, a carga é feita uma vez por pixel em `injectTikTokPixel`.
    appendScript('tiktok-base', '!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)}}(window,document,\'ttq\');');
}

function injectTikTokPixel(pixelId) {
    ensureTikTokBase();

    window.ttq.load(pixelId);
    window.ttq.instance(pixelId).page();
}

const injectors = {
    meta: injectMetaPixel,
    google: injectGooglePixel,
    tiktok: injectTikTokPixel,
};

function round(value) {
    return Math.round(Number(value) * 100) / 100;
}

function hasValue(details) {
    return Number.isFinite(Number(details.value));
}

/**
 * Identificador do evento compartilhado entre navegador e servidor. Enquanto a
 * Conversions API não existe ele é inofensivo; quando existir, é ele que faz a
 * Meta e o TikTok entenderem que o evento do servidor e o do navegador são a
 * mesma compra, em vez de contarem a conversão duas vezes.
 */
function buildEventId(eventKey, orderId) {
    if (orderId) {
        return `${eventKey}.${orderId}`;
    }

    return `${eventKey}.${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
}

function fireMetaEvent(pixelId, eventKey, details, eventId) {
    if (!window.fbq) {
        return;
    }

    const params = {};

    if (hasValue(details)) {
        params.value = round(details.value);
        params.currency = CURRENCY;
    }

    if (details.contentIds?.length) {
        params.content_ids = details.contentIds.map(String);
        params.content_type = 'product';
    }

    if (Number.isFinite(Number(details.numItems))) {
        params.num_items = Number(details.numItems);
    }

    window.fbq('trackSingle', pixelId, eventKey, params, { eventID: eventId });
}

function fireGoogleEvent(pixelId, eventKey, details) {
    if (!window.gtag) {
        return;
    }

    // `send_to` é obrigatório aqui por dois motivos: sem ele o evento vai para
    // todos os destinos configurados na página — inclusive o GTM da própria
    // plataforma — e, no Google Ads, conversão sem destino nem chega a ser
    // registrada.
    const params = { send_to: pixelId };

    if (hasValue(details)) {
        params.value = round(details.value);
        params.currency = CURRENCY;
    }

    if (details.orderId) {
        params.transaction_id = String(details.orderId);
    }

    window.gtag('event', googleEventMap[eventKey] || eventKey, params);
}

function fireTikTokEvent(pixelId, eventKey, details, eventId) {
    if (!window.ttq?.instance) {
        return;
    }

    const properties = {};

    if (hasValue(details)) {
        properties.value = round(details.value);
        properties.currency = CURRENCY;
    }

    if (details.contentIds?.length) {
        properties.contents = details.contentIds.map(id => ({
            content_id: String(id),
            content_type: 'product',
        }));
    }

    window.ttq.instance(pixelId).track(
        tiktokEventMap[eventKey] || eventKey,
        properties,
        { event_id: eventId }
    );
}

const eventFirers = {
    meta: fireMetaEvent,
    google: fireGoogleEvent,
    tiktok: fireTikTokEvent,
};

function isSupportedPixel(pixel) {
    const format = PIXEL_ID_FORMATS[pixel?.platform];

    return Boolean(format) && format.test(String(pixel?.pixel_id ?? ''));
}

export function injectPixels(pixels) {
    if (!Array.isArray(pixels)) {
        return;
    }

    storedPixels = pixels.filter(isSupportedPixel);

    storedPixels.forEach(pixel => {
        const key = `${pixel.platform}:${pixel.pixel_id}`;

        if (injectedPixels.has(key)) {
            return;
        }

        injectedPixels.add(key);
        injectors[pixel.platform](pixel.pixel_id);
    });
}

/**
 * Dispara uma conversão nos pixels do evento que assinaram esse gatilho.
 *
 * @param {string} eventKey Chave do gatilho como o produtor a escolheu no painel
 *        (`AddToCart`, `InitiateCheckout`, `AddPaymentInfo`, `Lead`, `Purchase`).
 * @param {object} [details]
 * @param {number} [details.value] Valor da conversão em reais. Sem ele a campanha
 *        conta a conversão mas não consegue otimizar por retorno.
 * @param {string|number} [details.orderId] Pedido. Vira `transaction_id` no Google
 *        e a chave de deduplicação com a Conversions API.
 * @param {Array<string|number>} [details.contentIds] IDs dos lotes comprados.
 * @param {number} [details.numItems] Quantidade total de itens.
 * @param {boolean} [details.once] Dispara no máximo uma vez por pedido. O
 *        `Purchase` do pix precisa disso: o `PixCode` é montado duas vezes (gaveta
 *        e collapse) e as duas instâncias ouvem o mesmo canal do Pusher.
 */
export function trackEvent(eventKey, details = {}) {
    const eventId = buildEventId(eventKey, details.orderId);

    if (details.once) {
        if (firedEvents.has(eventId)) {
            return;
        }

        firedEvents.add(eventId);
    }

    storedPixels.forEach(pixel => {
        if (!pixel.tracking_events?.includes(eventKey)) {
            return;
        }

        eventFirers[pixel.platform](pixel.pixel_id, eventKey, details, eventId);
    });
}

/**
 * Traduz os lotes do carrinho (`[{ id, quantity }]`) para os campos de conteúdo
 * que as três plataformas entendem. Fica aqui, e não no checkout, porque o
 * formato é uma exigência do rastreamento, não do pedido.
 */
export function cartDetails(batches) {
    const items = Array.isArray(batches) ? batches : [];

    return {
        contentIds: items.map(batch => batch.id),
        numItems: items.reduce((total, batch) => total + Number(batch.quantity || 0), 0),
    };
}

export function cleanupPixels() {
    const scripts = document.querySelectorAll(`[${PIXEL_ATTR}]`);
    scripts.forEach(script => script.remove());

    storedPixels = [];
    injectedPixels.clear();
    firedEvents.clear();
}
